/**
 * Resolving a ViewerContext from what the app knows about the current viewer and page.
 * Pure helpers only — the React wiring lives in `useVisibilityContext`.
 */

import { calculateAge } from '@/helpers/profileHelpers';
import type { UserModel } from '@/apiTypes/UserTypes';
import { AGE_BUCKETS, type AgeBucket, type Identity } from './visibilityTypes';
import type { ViewerContext } from './evaluateVisibility';

/**
 * Two letter codes an `include-states` / `exclude-states` rule can name, for the simulation
 * dropdown on the diagnostics page. Static rather than read from the states API so the picker
 * works even when that request fails, and so it cannot shift under a test.
 */
export const US_STATE_CODES: string[] = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
    'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
    'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
    'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    // Territories, which the profile list also carries.
    'AS', 'GU', 'MP', 'PR', 'VI',
];

/**
 * Page names editors may type into `include-pages` / `exclude-pages`.
 *
 * ASA-1570 open question 1: these are ours to maintain, because a typo in Strapi silently
 * hides content. Exported so the list can back a picker in the CMS later instead of a free
 * text field. Keys are exact pathnames — the spec matches the page only, never sub-pages,
 * so `/library/articles/x` is NOT the `library` page. Use `include-url` for subtrees.
 */
export const CMS_PAGE_NAMES: Record<string, string> = {
    '/': 'home',
    '/dashboard': 'dashboard',
    '/library': 'library',
    '/explore': 'explore',
    '/explore/careers': 'careers',
    '/skills': 'skills',
    '/profile': 'profile',
    '/assessments': 'assessments',
    '/journeys': 'journeys',
    '/get-started': 'get-started',
    '/faqs': 'faqs',
    '/contact-us': 'contact-us',
    '/privacy-policy': 'privacy-policy',
    '/terms-of-use': 'terms-of-use',
    '/cms-zones': 'cms-zones',
    '/cms-visibility': 'cms-visibility',
};

/** Known page names, for editor docs and (later) a CMS picker. */
export const KNOWN_PAGE_NAMES: string[] = [...new Set(Object.values(CMS_PAGE_NAMES))].sort();

/**
 * Page name for a pathname. Unmapped paths fall back to the full path minus its leading
 * slash, which will simply not match any authored rule — the safe direction.
 */
export function resolvePageName(pathname: string): string {
    const normalized = pathname.length > 1 && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
    return CMS_PAGE_NAMES[normalized] ?? normalized.replace(/^\//, '');
}

/**
 * Age bucket from the profile.
 *
 * ASA-1570 gotcha: we store a birth date, not the spec's buckets, so the mapping is ours.
 * Returns null — meaning "unknown age" — when there is no usable birth date, when the
 * account is flagged `restrictedDueToAge`, or when the viewer is younger than the lowest
 * bucket. A null age is never matched by `include-ages` and never removed by `exclude-ages`.
 *
 * Boundary behavior: the bucket follows the viewer's age today, so a birthday moves a
 * viewer between buckets on the day it happens (`calculateAge` already handles this).
 */
export function resolveAgeBucket(user: UserModel | null | undefined): AgeBucket | null {
    const profile = user?.userProfile;
    if (!profile) return null;
    if (profile.restrictedDueToAge) return null;

    let age: number | null = null;
    if (profile.dateOfBirth) {
        const parsed = new Date(profile.dateOfBirth);
        if (!Number.isNaN(parsed.getTime())) {
            age = calculateAge(parsed);
        }
    }
    // Coarser fallback for profiles carrying only a birth year (no month/day to compare).
    if (age == null && typeof profile.birthYear === 'number' && profile.birthYear > 0) {
        age = new Date().getFullYear() - profile.birthYear;
    }
    if (age == null || Number.isNaN(age)) return null;

    if (age < 13) return null;
    if (age <= 15) return AGE_BUCKETS[0];
    if (age <= 18) return AGE_BUCKETS[1];
    if (age <= 24) return AGE_BUCKETS[2];
    return AGE_BUCKETS[3];
}

export function resolveIdentity(user: UserModel | null | undefined): Identity {
    return user?.id ? 'registered' : 'visitors';
}

/**
 * Non-production overrides, so the age and state axes can be exercised without editing a
 * profile for every case: `?vis-identity=visitors&vis-ages=13-15&vis-states=MA&vis-page=home`.
 * Callers gate this on a non-production host (see useVisibilityContext).
 */
export function applyVisibilityOverrides(ctx: ViewerContext, search: string): ViewerContext {
    const params = new URLSearchParams(search);
    if ([...params.keys()].every(k => !k.startsWith('vis-'))) return ctx;

    const identity = params.get('vis-identity');
    const ages = params.get('vis-ages');
    const state = params.get('vis-states');
    const page = params.get('vis-page');
    const url = params.get('vis-url');

    return {
        ...ctx,
        ...(identity === 'visitors' || identity === 'registered' ? { identity } : {}),
        ...(ages != null
            ? { ageBucket: (AGE_BUCKETS as readonly string[]).includes(ages) ? ages as AgeBucket : null }
            : {}),
        ...(state != null ? { stateCode: state ? state.toUpperCase() : null } : {}),
        ...(page != null ? { page } : {}),
        ...(url != null ? { url } : {}),
    };
}
